(ns ductile.ui.agent.artifacts.table
  (:require [clojure.string :as str]
            [ductile.base64u :as base64]
            [ductile.ui.components.table :as table]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.router :as router]
            [ductile.ui.vehicle-search :as vehicle-search]
            [reagent.core :as r]))

(def ^:private preferred-vehicle-search-column-order
  [:vehicle/vin :vehicle/model :entry/datetime :customer/name])

(defn- safe-table-value [v]
  (cond
    (map-entry? v) (pr-str v)
    (map? v) (pr-str v)
    (set? v) (pr-str v)
    (and (sequential? v) (not (string? v))) (pr-str v)
    :else v))

(defn rows-from-data
  [data]
  (cond
    (nil? data) []
    (and (sequential? data) (not (string? data))) (vec data)
    (map? data) (mapv (fn [[k v]] {:key (safe-table-value k)
                                   :value (safe-table-value v)})
                      data)
    :else [{:value (safe-table-value data)}]))

(defn- present-cols-for-row [row]
  (->> vehicle-search/default-columns
       (mapcat (fn [k]
                 (cond
                   (contains? row k) [k]
                   (contains? row (name k)) [k]
                   :else [])))
       vec))

(defn- normalize-col-key [k]
  (if (string? k) (keyword k) k))

(defn- order-visible-cols [cols]
  (let [cols (vec cols)
        cols-set (set cols)
        lead-cols (filter cols-set preferred-vehicle-search-column-order)
        trailing-cols (remove (set preferred-vehicle-search-column-order) cols)]
    (vec (concat lead-cols trailing-cols))))

(defn- project-row-for-table [row cols]
  (reduce (fn [acc col]
            (let [col-name (when (or (keyword? col) (symbol? col) (string? col))
                             (name col))
                  v (cond
                      (contains? row col) (get row col)
                      (contains? row (str col)) (get row (str col))
                      (and col-name (contains? row col-name)) (get row col-name)
                      :else nil)]
              (assoc acc col (safe-table-value v))))
          {}
          cols))

(defn- map-get-any
  [m k]
  (when (map? m)
    (or (get m k)
        (when (keyword? k)
          (or (get m (name k))
              (get m (str k))))
        (when (string? k)
          (or (get m (keyword k))
              (get m (str ":" k)))))))

(defn row->vehicle-vin
  [row]
  (let [vin (or (map-get-any row :vehicle/vin)
                (map-get-any row "vehicle/vin"))]
    (when (and (string? vin) (not (str/blank? vin)))
      vin)))

(def ^:private default-page-size 100)

(defn view
  [{:keys [payload]} {:keys [dataset-data on-row-click]}]
  (let [!page (r/atom 0)
        page-size (or (:page-size payload) default-page-size)]
    (fn [{:keys [payload]} {:keys [dataset-data on-row-click]}]
      (let [rows (if-let [dataset-id (:dataset-id payload)]
                   (rows-from-data (dataset-data dataset-id))
                   (vec (or (:rows payload) [])))
            total-rows (count rows)
            page @!page
            start-idx (* page page-size)
            end-idx (min (+ start-idx page-size) total-rows)
            paged-rows (subvec rows start-idx end-idx)
            total-pages (max 1 (int (Math/ceil (/ total-rows page-size))))
            visible-cols (let [default-cols (->> rows (mapcat present-cols-for-row) distinct vec)
                               all-cols (->> rows
                                             (filter map?)
                                             (mapcat keys)
                                             (map normalize-col-key)
                                             distinct
                                             vec)
                               base-cols (if (seq default-cols)
                                           (vec (concat default-cols (remove (set default-cols) all-cols)))
                                           all-cols)]
                           (order-visible-cols base-cols))
            table-rows (cond
                         (and (seq visible-cols) (every? map? paged-rows))
                         (mapv #(project-row-for-table % visible-cols) paged-rows)
                         (every? map? paged-rows)
                         (mapv #(into {} (for [[k v] %] [k (safe-table-value v)])) paged-rows)
                         :else paged-rows)
            drilldown-enabled? (and on-row-click (some row->vehicle-vin paged-rows))
            opts (cond-> {:column-order visible-cols}
                   drilldown-enabled? (assoc :row-action (fn [row]
                                                           (when-let [vin (row->vehicle-vin row)]
                                                             (on-row-click vin row)))))
            search-params (:search-params payload)
            compound-name (get search-params :compound/name)
            vehicle-search-url (when (and (seq search-params) compound-name)
                                 (router/path '[compound :compound/name vehicle-search]
                                              {:compound/name compound-name}
                                              (str "b64=" (base64/encode (pr-str search-params)))))]
        [:div {:class "max-h-[60vh] overflow-y-auto overflow-x-auto overscroll-y-contain overscroll-x-contain"}
         (when vehicle-search-url
           [:div.p-2.bg-blue-50.border-b.text-sm
            [:a {:href vehicle-search-url
                 :target "_blank"
                 :class "text-blue-600 hover:text-blue-800 hover:underline"}
             (tr "Open in vehicle search")]])
         (when (> total-rows page-size)
           [:div.flex.items-center.gap-2.p-2.bg-slate-50.border-b.text-sm
            [:span (tr "Showing %1 - %2 of %3 rows" (inc start-idx) end-idx total-rows)]
            [:div.flex.gap-1
             [:button {:class (if (zero? page) "opacity-50 cursor-not-allowed" "hover:bg-slate-200")
                       :disabled (zero? page)
                       :on-click #(swap! !page dec)}
              "← " (tr "Previous")]
             [:span "|"]
             [:button {:class (if (>= (inc page) total-pages) "opacity-50 cursor-not-allowed" "hover:bg-slate-200")
                       :disabled (>= (inc page) total-pages)
                       :on-click #(swap! !page inc)}
              (tr "Next") " →"]]])
         [table/table opts table-rows]]))))
